import { useEffect } from "react";
import { UseMutateFunction, useMutation } from "@tanstack/react-query";
import { CheckAccessRequestParams } from "../query.types";
import { CheckApiAccessResponseModel } from "../response.types";
import { appStore } from "entities/App";
import { searchFilterStore } from "entities/SearchFilter";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { isNullable } from "shared/lib/typeCheck.lib";
import { NullableValues } from "shared/lib/typeCheck.lib";
import { isAppIntegration } from "shared/utils/url.utils";

import { apiAccessService } from "../api";

type UseCheckIntegrationAccessMutationReturnType = {
  checkSuccessResponse: CheckApiAccessResponseModel | undefined;
  checkAccess: UseMutateFunction<CheckApiAccessResponseModel, Error, CheckAccessRequestParams>;
  isCheckAccessSuccess: boolean;
  isCheckAccessError: boolean;
  isCheckAccessLoading: boolean;
};

type UseCheckApiAccessMutationParams = NullableValues<Pick<CheckAccessRequestParams, "platformId">> & {
  checkOnMount?: boolean;
};

const useCheckApiAccessMutation = ({
  checkOnMount = false,
  platformId,
}: UseCheckApiAccessMutationParams): UseCheckIntegrationAccessMutationReturnType => {
  const dispatch = useAppDispatch();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);

  const { data, mutate, isError, isPending, isSuccess } = useMutation<
    CheckApiAccessResponseModel,
    Error,
    CheckAccessRequestParams
  >({
    mutationFn: apiAccessService.checkAccess,
  });

  useEffect(() => {
    if (!checkOnMount) return;
    if (!isAppIntegration || isNullable(platformId)) return;

    mutate(
      { platformId, shipmentType: shipment },
      {
        onSuccess: (response) => {
          dispatch(appStore.actions.setAppIntegrationHasApiAccess(response.has_access));
        },
      }
    );
  }, [checkOnMount, shipment]);

  return {
    checkSuccessResponse: data,
    checkAccess: mutate,
    isCheckAccessSuccess: isSuccess,
    isCheckAccessError: isError,
    isCheckAccessLoading: isPending,
  };
};

export { useCheckApiAccessMutation };
